import { SectionHeader } from '../common/section-header'
import { Link } from 'react-router-dom'

import { Badge, FieldHelp, Notice } from '@speaking-coach/ui'
import { CONTENT_HELP, ContentMetricLabel, sentencePercentileHelp } from './content-help.js'
import {
  aboveBandPolicy,
  allTopics,
  targetCount,
  topicCount,
  topicHref,
} from './content-model.js'
import { ContentRoute, ContentShell } from './content-shell.js'
import { ContentScopeFilters, useContentScope } from './content-scope.js'
import { Evidence } from './topic-page.js'
import type { ContentDashboard, ContentLevel } from './types.js'

export function ContentCoveragePage(): JSX.Element {
  return (
    <ContentRoute>
      {(dashboard) => <CoveragePage dashboard={dashboard} />}
    </ContentRoute>
  )
}

function CoveragePage({
  dashboard,
}: {
  dashboard: ContentDashboard
}): JSX.Element {
  const { selectedLevel } = useContentScope(dashboard)
  return <ContentShell title="Coverage" filters={<ContentScopeFilters dashboard={dashboard} required />}>
    {selectedLevel ? <CoverageReport dashboard={dashboard} selectedLevel={selectedLevel} /> : <Notice variant="neutral">Select a Program and level to inspect coverage.</Notice>}
  </ContentShell>
}

function CoverageReport({ dashboard, selectedLevel }: { dashboard: ContentDashboard; selectedLevel: ContentLevel }): JSX.Element {
  const aboveBandPercent = selectedLevel.vocabulary.aboveBandRate * 100
  const aboveBandLevelCap = selectedLevel.band.aboveBandLevelPercent
  const aboveBandOverCap =
    aboveBandLevelCap !== null && aboveBandPercent > aboveBandLevelCap
  const aboveBandWaivedCount = selectedLevel.themes.reduce(
    (total, theme) =>
      total +
      theme.topics.reduce(
        (sum, topic) =>
          sum +
          topic.targetVocab.filter((item) => item.aboveBand && item.waived)
            .length,
        0,
      ),
    0,
  )
  const registeredExceptionCount = `${aboveBandWaivedCount} approved exception${aboveBandWaivedCount === 1 ? '' : 's'}`
  const aboveBandNote =
    aboveBandLevelCap === null
      ? `${registeredExceptionCount} · C1+ targets need exceptions`
      : `${registeredExceptionCount} · ${
          aboveBandOverCap ? 'over' : 'within'
        } ${aboveBandLevelCap}% level cap`
  const vocabularyPath = (params: Record<string, string> = {}): string =>
    `/admin/content/vocabulary?${new URLSearchParams({
      level: selectedLevel.programLevelId,
      ...params,
    }).toString()}`

  return (
      <section aria-label="Coverage report" className="space-y-4">
        <section aria-labelledby="level-overview-heading" className="space-y-3">
          <SectionHeader
            level={3}
            id="level-overview-heading"
            title={<>{selectedLevel.level} overview</>}
            description={selectedLevel.programLevelContentKey}
            meta={<VocabularyDistribution level={selectedLevel} />}
          />
          {(selectedLevel.vocabulary.invalidCount ?? 0) > 0 ? (
            <Notice variant="warning">
              {selectedLevel.vocabulary.invalidCount} invalid vocabulary entries excluded from grading totals.{' '}
              <Link className="font-medium underline" to={vocabularyPath({ view: 'invalid' })}>Inspect invalid entries</Link> and correct the source content.
            </Notice>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Lessons"
              value={topicCount(selectedLevel).toString()}
            />
            <MetricCard
              label="Target slots"
              value={targetCount(selectedLevel).toString()}
              note="slot occurrences across this level"
              to={vocabularyPath()}
              help={CONTENT_HELP.targetSlots}
            />
            <MetricCard
              label="Above-band target slots"
              value={`${selectedLevel.vocabulary.aboveBandCount} of ${selectedLevel.vocabulary.totalLemmas} · ${aboveBandPercent.toFixed(1)}%`}
              note={aboveBandNote}
              attention={aboveBandOverCap}
              to={vocabularyPath({ view: 'above' })}
              help={CONTENT_HELP.aboveBandTargetSlots}
            />
            <MetricCard
              label="Hard-cap breaches"
              value={selectedLevel.sentenceStats.aboveHardCount.toString()}
              attention={selectedLevel.sentenceStats.aboveHardCount > 0}
              help={CONTENT_HELP.hardCapBreaches}
            />
          </div>
          <LevelSentenceSummary level={selectedLevel} />
          <p className="text-caption text-text-secondary">
            Band policy: at least {selectedLevel.band.inWindowQuota} in-window
            targets per lesson · {aboveBandPolicy(selectedLevel)} ·{' '}
            {selectedLevel.band.newMovesPerMission} new move per mission ·
            Japanese support {selectedLevel.band.jaSupport.toLowerCase()}.
          </p>
        </section>
        <Repeats dashboard={dashboard} level={selectedLevel} />
      </section>
  )
}

function MetricCard({
  label,
  value,
  note,
  attention = false,
  to,
  help,
}: {
  label: string
  value: string
  note?: string
  attention?: boolean
  to?: string
  help?: string
}): JSX.Element {
  const valueContent = (
    <>
      <p className={`mt-1 text-product-title font-semibold ${attention ? 'text-danger-text' : ''}`}>
        {value}
      </p>
      {note ? <p className="mt-1 text-caption text-text-secondary">{note}</p> : null}
    </>
  )
  const classes =
    'rounded-token-card border border-border-default bg-surface-elev p-4'
  return to ? (
    <div className={`${classes} relative transition-colors hover:border-accent`}>
      <Link
        to={to}
        aria-label={[label, value, note].filter(Boolean).join(' ')}
        className="absolute inset-0 rounded-token-card focus-visible:focus-ring"
      />
      <div className="pointer-events-none relative">
        <ContentMetricLabel label={label} help={help} uppercase />
        {valueContent}
      </div>
    </div>
  ) : (
    <div className={classes}>
      <ContentMetricLabel label={label} help={help} uppercase />
      {valueContent}
    </div>
  )
}

function LevelSentenceSummary({ level }: { level: ContentLevel }): JSX.Element {
  const quotesMalformed =
    level.sentenceStats.unbalancedQuotes ||
    level.sentenceStats.spansCrossingBlankLine > 0
  return (
    <section
      aria-label={`${level.level} sentence distribution`}
      data-testid="content-level-sentence-stats"
      className="rounded-token-card border border-border-default bg-surface-elev p-4"
    >
      <SectionHeader
        level={3}
        title="Level sentence distribution"
        meta={<FieldHelp label="sentence percentiles" size="compact">
          {CONTENT_HELP.sentencePercentiles}
        </FieldHelp>}
      />
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Evidence
          label="Sample"
          value={`${level.sentenceStats.sentenceCount} sentences`}
          help={CONTENT_HELP.sample}
        />
        <Evidence
          label="Level p50"
          value={`${level.sentenceStats.p50Words} words`}
          help={sentencePercentileHelp(50)}
        />
        <Evidence
          label="Level p90"
          value={`${level.sentenceStats.p90Words} words`}
          help={sentencePercentileHelp(90)}
        />
        <Evidence
          label="Level p95"
          value={`${level.sentenceStats.p95Words} words`}
          help={sentencePercentileHelp(95)}
        />
        <Evidence
          label="Level max"
          value={`${level.sentenceStats.maxWords} words`}
          help={CONTENT_HELP.sentenceMaximum}
        />
        <Evidence
          label="Advisory / hard caps"
          value={`${level.band.advisorySentenceWords} / ${level.band.hardSentenceWords} words`}
          help={CONTENT_HELP.advisoryAndHardCaps}
        />
        <Evidence
          label="Above caps"
          value={`${level.sentenceStats.aboveAdvisoryCount} advisory · ${level.sentenceStats.aboveHardCount} hard`}
          attention={
            level.sentenceStats.aboveAdvisoryCount > 0 ||
            level.sentenceStats.aboveHardCount > 0
          }
          help={CONTENT_HELP.aboveCaps}
        />
        <Evidence
          label="Quote integrity"
          value={quotesMalformed ? 'Malformed quotes; stats unreliable' : 'All quotes measurable'}
          attention={quotesMalformed}
          help={CONTENT_HELP.quoteIntegrity}
        />
      </div>
    </section>
  )
}

function VocabularyDistribution({ level }: { level: ContentLevel }): JSX.Element {
  const totalSlots = targetCount(level)
  return (
    <div
      className="flex flex-wrap items-center gap-1 text-caption"
      aria-label={`CEFR grade distribution across ${totalSlots} target slots`}
    >
      <FieldHelp label="CEFR grade distribution" size="compact">
        {CONTENT_HELP.gradeDistribution}
      </FieldHelp>
      {Object.entries(level.vocabulary.distribution).map(([grade, count]) => (
        <Link
          key={grade}
          to={`/admin/content/vocabulary?${new URLSearchParams({
            level: level.programLevelId,
            ...(grade === 'ungraded' ? { view: 'ungraded' } : { grade }),
          }).toString()}`}
          aria-label={`${grade === 'ungraded' ? 'Ungraded' : `CEFR grade ${grade}`}: ${count} target slots`}
        >
          <Badge variant={grade === 'ungraded' ? 'danger' : 'muted'}>
            {grade === 'ungraded' ? 'Ungraded' : `Grade ${grade}`} · {count} slots
          </Badge>
        </Link>
      ))}
    </div>
  )
}

function Repeats({
  dashboard,
  level,
}: {
  dashboard: ContentDashboard
  level: ContentLevel
}): JSX.Element {
  const repeats = dashboard.crossProgramRepeats.filter((repeat) =>
    repeat.topics.some((topic) => topic.programLevelId === level.programLevelId),
  )
  const locations = allTopics(dashboard)

  return (
    <section
      data-testid="content-repeats"
      className="rounded-token-card border border-border-default bg-surface-elev p-5"
    >
      <SectionHeader title={<>Repeats across program levels involving {level.programLevelContentKey}</>} meta={<Badge variant="muted">{repeats.length} lemmas</Badge>} />
      <p className="mt-1 text-body text-text-secondary">
        Compare every reuse that touches this level, then open either lesson for
        context.
      </p>
      {repeats.length === 0 ? (
        <p className="mt-4 text-body text-text-secondary">
          No repeats across program levels involve {level.programLevelContentKey}.
        </p>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {repeats.map((repeat) => (
            <div
              key={repeat.lemma}
              className="rounded-token-card border border-border-default bg-surface-app p-3"
            >
              <p className="font-semibold">{repeat.lemma}</p>
              <ul className="mt-2 space-y-2 text-caption text-text-secondary">
                {repeat.topics.map((topic) => {
                  const location = locations.find(
                    (row) => row.topic.id === topic.id,
                  )
                  const label = (
                    <>
                      <Badge variant={topic.programLevelId === level.programLevelId ? 'neutral' : 'muted'}>
                        {topic.level}
                      </Badge>
                      <span>
                        #{topic.position} · {topic.title}
                      </span>
                    </>
                  )
                  return (
                    <li key={topic.id}>
                      {location ? (
                        <Link
                          to={topicHref(location, undefined, 'coverage', level.programLevelId)}
                          className="flex items-center gap-2 rounded-token-input p-1 hover:bg-surface-elev hover:text-text-primary"
                        >
                          {label}
                        </Link>
                      ) : (
                        <span className="flex items-center gap-2 p-1">{label}</span>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
